import React, { useMemo, useState } from "react";

import { Box, Skeleton, Stack, Typography } from "@mui/material";

import {
    deleteDynamicField,
    updateDynamicField,
} from "helpers/leads/leadConnection/postConnectionField";

import { GenericModal } from "components/GenericModal";
import CreateDynamicValueModal from "components/organisms/CreateDynamicValueModal";
import SwalAlertComp from "components/SwalAlert";

import DeleteDynamicFieldDialog from "./components/DeleteDynamicFieldDialog";
import DynamicFieldCard from "./components/DynamicFieldCard";
import {
    BasicModePayloadItem,
    DynamicField,
    FunctionOption,
    Language,
    SourceFieldOption,
} from "./DynamicFieldsSection.types";

interface DynamicFieldsSectionProps {
    open: boolean;
    onClose: () => void;
    dynamicFields: DynamicField[];
    loading: boolean;
    sourceFieldOptions: SourceFieldOption[];
    allFunctions: FunctionOption[];
    language?: Language;
    /** Called after a successful update or delete so the parent can refetch data. */
    onRefresh: () => void;
    /** Called after a successful delete so the parent can adjust edit-mode cancel behavior. */
    onDynamicFieldDeleted?: () => void;
}

interface EditedFieldShape {
    name: string;
    fields?: Array<{
        value: string;
        label: string;
        prefix?: string;
        suffix?: string;
        defaultValue?: string;
        fn?: Array<{ fn_name: string; fn_values?: Record<string, any> }>;
    }>;
    payload?: string;
    mode?: "basic" | "advanced";
}

const DynamicFieldsSection: React.FC<DynamicFieldsSectionProps> = ({
    open,
    onClose,
    dynamicFields,
    loading,
    sourceFieldOptions,
    allFunctions,
    language,
    onRefresh,
    onDynamicFieldDeleted,
}) => {
    const [editingField, setEditingField] = useState<DynamicField | null>(null);
    const [deletingField, setDeletingField] = useState<DynamicField | null>(
        null,
    );

    const editedFieldForModal = useMemo<EditedFieldShape | undefined>(() => {
        if (!editingField) return undefined;

        if (
            editingField.type === "basic_mode" &&
            Array.isArray(editingField.payload)
        ) {
            return {
                name: editingField.name,
                mode: "basic",
                fields: (editingField.payload as BasicModePayloadItem[]).map(
                    (item) => {
                        const matched = sourceFieldOptions.find(
                            (opt) =>
                                opt.value === item.key || opt.key === item.key,
                        );
                        return {
                            value: matched?.value || item.key,
                            label: matched?.label || item.key,
                            prefix: item.prefix || "",
                            suffix: item.suffix || "",
                            defaultValue: item.default || "",
                            fn: (item.fn || []).map((fnItem: any) =>
                                typeof fnItem === "string"
                                    ? { fn_name: fnItem, fn_values: {} }
                                    : fnItem,
                            ),
                        };
                    },
                ),
            };
        }

        const advancedPayload =
            typeof editingField.payload === "string"
                ? editingField.payload
                : "";
        const tokenPairs = sourceFieldOptions
            .filter((opt) => opt.key && opt.value && opt.key !== opt.value)
            .map((opt) => ({
                from: `{{${opt.key}}}`,
                to: `{{${opt.value}}}`,
            }))
            .sort((a, b) => b.from.length - a.from.length);

        return {
            name: editingField.name,
            mode: "advanced",
            payload: tokenPairs.reduce(
                (text, pair) => text.split(pair.from).join(pair.to),
                advancedPayload,
            ),
        };
    }, [editingField, sourceFieldOptions]);

    const otherFieldNames = useMemo(
        () =>
            dynamicFields
                .filter((field) => field.id !== editingField?.id)
                .map((field) => field.name),
        [dynamicFields, editingField],
    );

    const handleEditSubmit = async (data: {
        name: string;
        fields?: Array<{
            value: string;
            prefix?: string;
            suffix?: string;
            defaultValue?: string;
            fn?: Array<{ fn_name: string; fn_values?: Record<string, any> }>;
        }>;
        payload?: string;
        mode: "basic" | "advanced";
    }): Promise<boolean> => {
        if (!editingField) return false;

        const payload =
            data.mode === "basic"
                ? {
                      dynamic_fields: {
                          basic_mode: {
                              payload: (data.fields || []).map((f) => ({
                                  key: f.value,
                                  default: f.defaultValue || null,
                                  prefix: f.prefix || null,
                                  suffix: f.suffix || null,
                                  fn: (f.fn || []).map((fnItem: any) =>
                                      typeof fnItem === "string"
                                          ? { fn_name: fnItem, fn_values: {} }
                                          : fnItem,
                                  ),
                              })),
                          },
                      },
                  }
                : {
                      dynamic_fields: {
                          advanced_mode: {
                              payload: data.payload,
                          },
                      },
                  };

        const response = await updateDynamicField(editingField.id, payload);
        if (!response.status) return false;

        SwalAlertComp({
            title: language?.delete_swal_modal?.success ?? "Success",
            message: "Dynamic field updated successfully",
            icon: "success",
            timer: 3000,
        });
        setEditingField(null);
        onRefresh();
        return true;
    };

    const handleDelete = async (dynamicFieldId: number, force: boolean) => {
        const response = await deleteDynamicField(dynamicFieldId, force);
        if (response.status) {
            onDynamicFieldDeleted?.();
            onRefresh();
            // Close list modal when the last field is removed
            if (dynamicFields.length <= 1) {
                onClose();
            }
        }
        return response;
    };

    const handleClose = () => {
        if (editingField || deletingField) return;
        onClose();
    };

    return (
        <>
            <GenericModal
                open={open}
                onClose={handleClose}
                title={
                    language?.connection_detail?.dynamic_fields ??
                    "Dynamic Fields"
                }
                maxWidth="sm"
                fullWidth
                content={
                    <Box sx={{ p: 3 }}>
                        {loading ? (
                            <Stack spacing={1.5}>
                                {[0, 1].map((index) => (
                                    <Skeleton
                                        key={index}
                                        variant="rounded"
                                        height={112}
                                        sx={{ borderRadius: "10px" }}
                                    />
                                ))}
                            </Stack>
                        ) : dynamicFields.length === 0 ? (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ py: 2 }}
                            >
                                No dynamic fields yet.
                            </Typography>
                        ) : (
                            <Stack spacing={1.5}>
                                {dynamicFields.map((field) => (
                                    <DynamicFieldCard
                                        key={field.id}
                                        field={field}
                                        onEdit={setEditingField}
                                        onDelete={setDeletingField}
                                    />
                                ))}
                            </Stack>
                        )}
                    </Box>
                }
            />

            {editingField && (
                <CreateDynamicValueModal
                    isOpen={!!editingField}
                    onClose={() => setEditingField(null)}
                    onSubmit={handleEditSubmit}
                    availableSourceFields={sourceFieldOptions}
                    allFunctions={allFunctions}
                    editedField={editedFieldForModal}
                    editFieldIndex={editingField.id}
                    notAvailableFieldNames={otherFieldNames}
                    nameReadOnly
                />
            )}

            <DeleteDynamicFieldDialog
                open={!!deletingField}
                onClose={() => setDeletingField(null)}
                dynamicField={deletingField}
                language={language}
                onDelete={handleDelete}
                onDeleteSuccess={() => setDeletingField(null)}
            />
        </>
    );
};

export default DynamicFieldsSection;
